import type { BatMetadataConfigEntry } from './common/types';
import { coreFields } from './common/coreFields';
import { locationFields } from './common/locationFields';
import { msg } from '@lingui/core/macro';
import { ghgCategoryField } from './common/sharedFields';

export const ghgRemovalMetadata = {
  displayName: msg({
    message: 'GHG removal',
    context: 'Activity data schema name for GHG removal',
  }),
  fields: {
    ...coreFields,
    ...locationFields,
    ghg_category_id: ghgCategoryField,
    ghg_concerned: {
      displayName: msg({
        message: 'GHG concerned',
        context: 'The display name for the GHG concerned field',
      }),
      externalDescription: msg({
        message: 'The greenhouse gas type removed in the project (e.g. CO2).',
        context: 'GHG concerned',
      }),
    },
    ghg_emission_quantity: {
      displayName: msg({
        message: 'Emissions: from removal',
        context: 'The display name for a metric',
      }),
      externalDescription: msg({
        message:
          'The quantity of greenhouse gases emitted from the removal project.',
        context: 'Emissions from removal',
      }),
    },
    ghg_emission_unit: {
      displayName: msg({
        message: 'Emissions from removal unit',
        context: 'The display name for emissions: from removal unit field',
      }),
      externalDescription: msg({
        message: 'The unit of the GHG emissions. (e.g. kgCO₂e).',
        context: 'Emissions from removal unit',
      }),
    },
    ghg_removal_quantity: {
      displayName: msg({
        message: 'Emissions: removed',
        context: 'The quantity of GHGs removed.',
      }),
      externalDescription: msg({
        message: 'The quantity of greenhouse gases removed.',
        context: 'GHG removal',
      }),
    },
    ghg_removal_unit: {
      displayName: msg({
        message: 'Emissions removed unit',
        context: 'The display name for emissions: removed unit field',
      }),
      externalDescription: msg({
        message: 'The unit of the GHG removals. (e.g. kgCO₂e).',
        context: 'Emissions removed unit',
      }),
    },
    is_nature_based: {
      displayName: msg({
        message: 'Is nature based',
        context: 'The display name for is nature based field',
      }),
      externalDescription: msg({
        message:
          'Whether the greenhouse gas removal relies on natural processes such as forestry or soil sequestration.',
      }),
    },
    removal_activity: {
      displayName: msg({
        message: 'Removal activity',
        context: 'The display name for removal activity field',
      }),
      externalDescription: msg({
        message:
          'The type of activity used to remove greenhouse gases from the atmosphere.',
      }),
    },
    removal_type: {
      displayName: msg({
        message: 'Removal type',
        context: 'The display name for removal type field',
      }),
      externalDescription: msg({
        message: 'The classification of the greenhouse gas removal method.',
      }),
    },
    reversals_quantity: {
      displayName: msg({
        message: 'Emissions: reversed',
        context: 'The quantity of GHGs reversed.',
      }),
      externalDescription: msg({
        message: 'The quantity of greenhouse gases reversed.',
        context: 'GHG reversals',
      }),
    },
    reversals_unit: {
      displayName: msg({
        message: 'Emissions reversed unit',
        context: 'The display name for emissions reversed unit field',
      }),
      externalDescription: msg({
        message: 'The unit of the GHG reversals. (e.g. kgCO₂e).',
        context: 'Emissions reversed unit',
      }),
    },
    storage_pool: {
      displayName: msg({
        message: 'Storage pool',
        context: 'The display name for storage pool field',
      }),
      externalDescription: msg({
        message:
          'The carbon storage reservoir where removed greenhouse gases are sequestered.',
      }),
    },
  },
} as const satisfies BatMetadataConfigEntry;
